import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';
import { Building2, LayoutDashboard } from 'lucide-react';

interface CompanyOption {
  id: string;
  name: string;
}

export function CompanySwitcher() {
  const {
    accessibleCompanyIds,
    activeCompanyId,
    switchCompanyContext,
    canAccess,
  } = useAuth();

  // Auto-oculta quando não há mais de 1 company acessível
  if (accessibleCompanyIds.length <= 1) return null;

  return (
    <CompanySwitcherInner
      accessibleCompanyIds={accessibleCompanyIds}
      activeCompanyId={activeCompanyId}
      switchCompanyContext={switchCompanyContext}
      canAccess={canAccess}
    />
  );
}

// Inner component separated to avoid calling useQuery before the guard
interface InnerProps {
  accessibleCompanyIds: string[];
  activeCompanyId: string | null;
  switchCompanyContext: (id: string | null) => Promise<void>;
  canAccess: (feature: string) => boolean;
}

function CompanySwitcherInner({
  accessibleCompanyIds,
  activeCompanyId,
  switchCompanyContext,
  canAccess,
}: InnerProps) {
  const { data: companies, isLoading } = useQuery<CompanyOption[]>({
    queryKey: ['switcher-companies', accessibleCompanyIds],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('companies')
        .select('id, name')
        .in('id', accessibleCompanyIds);
      if (error) throw error;
      return data ?? [];
    },
    staleTime: 60 * 60 * 1000, // 1h — nomes de empresas raramente mudam
    enabled: accessibleCompanyIds.length > 0,
  });

  const canViewConsolidated = canAccess('org:view_consolidated');

  // Não exibir flash enquanto carrega nomes
  if (isLoading || !companies) return null;

  const shortName = (name: string) =>
    name.length > 16 ? name.slice(0, 14) + '…' : name;

  return (
    <div
      className="flex items-center gap-1 flex-wrap"
      style={{ maxWidth: 200 }}
    >
      {companies.map((company) => {
        const isActive = activeCompanyId === company.id;
        return (
          <button
            key={company.id}
            onClick={() => switchCompanyContext(company.id)}
            title={company.name}
            className={cn(
              'flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium transition-colors border',
              isActive
                ? 'bg-primary text-primary-foreground border-primary shadow-sm'
                : 'bg-muted/50 text-muted-foreground border-border hover:bg-accent hover:text-accent-foreground'
            )}
          >
            <Building2 className="h-3 w-3 shrink-0" />
            {shortName(company.name)}
          </button>
        );
      })}

      {canViewConsolidated && (
        <button
          onClick={() => switchCompanyContext(null)}
          title="Ver dados consolidados do grupo"
          className={cn(
            'flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium transition-colors border',
            activeCompanyId === null
              ? 'bg-primary text-primary-foreground border-primary shadow-sm'
              : 'bg-muted/50 text-muted-foreground border-border hover:bg-accent hover:text-accent-foreground'
          )}
        >
          <LayoutDashboard className="h-3 w-3 shrink-0" />
          Consolidado
        </button>
      )}
    </div>
  );
}
